import * as React from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { createSearchParams, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useZocksIntercom } from '../../components/Intercom/hooks/useZocksIntercom'
import { CreateUserIntegrationPayload, useCreateUserIntegrationMutation } from '../../store/user/user'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { selectAccountId } from '../../store/auth/authSlice'
import { createState, IntegrationServiceId, readState } from '../../common/integrations'
import { addToast } from '../../store/toast/toastSlice'
import { TrackEvent, useTracking } from '../../apps/professional-app/fa/common/tracking'
import { ROUTER_BASEPATH } from '../../common/constants'
import { RedirectProgress } from '../../components/RedirectProgress/RedirectProgress'

type OAuthParams = {
  name: string
  required: boolean
  alias?: string
}

const _VALID_ZOHO_OAUTH_PARAMS: OAuthParams[] = [
  { name: 'location', required: false, alias: 'region' },
  { name: 'accounts-server', required: false, alias: 'accountsServer' },
]

const _VALID_OAUTH_PARAMS: {
  [key in keyof typeof IntegrationServiceId | string]: OAuthParams[]
} = {
  general: [{ name: 'code', required: true }],
  [IntegrationServiceId.ZOHO]: _VALID_ZOHO_OAUTH_PARAMS,
}

const _INTEGRATION_TYPE_MAP: { [key: string]: string } = {
  eMoney: 'emoney',
}

const IntegrationCallbackView: React.FC = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { integrationType = IntegrationServiceId.ZOOM } = useParams()
  const accountId = useAppSelector(selectAccountId)
  const { booted } = useZocksIntercom()
  const [searchParams, setSearchParams] = useSearchParams()
  const [createUserIntegration, { isSuccess }] = useCreateUserIntegrationMutation()
  const [redirecting, setRedirecting] = useState(false)
  const { tracked } = useTracking()

  const _createNewUserIntegration = useCallback(
    (accountId: string, integrationType: IntegrationServiceId, payload: CreateUserIntegrationPayload) => {
      createUserIntegration({
        accountId,
        integrationName: integrationType,
        payload,
      })
    },
    [createUserIntegration],
  )

  const createNewUserIntegration = useMemo(
    () =>
      tracked(_createNewUserIntegration, {
        eventName: TrackEvent.CONNECT_TO_SYSTEM,
        eventDetails: { serviceId: integrationType },
      }),
    [tracked, _createNewUserIntegration, integrationType],
  )

  const handleError = useCallback(() => {
    const error = searchParams.get('error')
    const errorDescription = searchParams.get('error_description')
    const errorMessage = error
      ? `Service error: ${error}` + (errorDescription ? `: ${errorDescription}` : '')
      : 'Unable to create integration. Please contact us at support@zocks.io'
    dispatch(
      addToast({
        variant: 'error',
        title: 'Failed to create integration',
        messages: [errorMessage],
        delay: 10000,
      }),
    )
    console.log(searchParams.toString())
    navigate('/account/profile-settings/integrations')
  }, [dispatch, searchParams, navigate])

  useEffect(() => {
    if (integrationType in _INTEGRATION_TYPE_MAP) {
      const mappedIntegrationType = _INTEGRATION_TYPE_MAP[integrationType]
      navigate({
        pathname: `/integrations/callback/${mappedIntegrationType}`,
        search: createSearchParams(searchParams).toString(),
      })
    }
  }, [integrationType, navigate, searchParams])

  useEffect(() => {
    if (isSuccess) {
      searchParams.delete('code')
      searchParams.delete('app')
      const state = searchParams.get('state')
      if (!state) {
        handleError()
        return
      }
      const stateData = readState(state, integrationType as IntegrationServiceId)
      if (!stateData) {
        handleError()
        return
      }

      const { redirectUri } = stateData
      setSearchParams(searchParams)
      setRedirecting(true)
      if (redirectUri!.endsWith('upcoming')) {
        window.location.href = `${redirectUri}?isNewIntegration`
      } else {
        window.location.href = `${redirectUri}`
      }
    }
  }, [isSuccess, searchParams, setSearchParams, integrationType, booted, handleError])

  const createPayloadFromSearchParams = useCallback(() => {
    const missingRequired: string[] = []
    const validParams = _VALID_OAUTH_PARAMS['general'].concat(_VALID_OAUTH_PARAMS[integrationType] || [])
    const result = validParams.reduce((result: { [key: string]: string }, { name, required, alias }) => {
      const param = searchParams.get(name)

      if (!param && required) {
        missingRequired.push(name)
      }

      if (param) {
        result[alias || name] = param
      }
      return result
    }, {})
    if (missingRequired.length > 0) {
      console.error(`Missing required parameters: ${missingRequired.join(', ')}`)
    }

    return result
  }, [searchParams, integrationType])

  useEffect(() => {
    if (redirecting || integrationType in _INTEGRATION_TYPE_MAP) {
      return
    }
    const { code, ...rest } = createPayloadFromSearchParams()
    const state = searchParams.get('state')
    if (!state) {
      handleError()
      return
    }
    const stateData = readState(state, integrationType as IntegrationServiceId)
    if (!stateData) {
      handleError()
      return
    }

    const { step, customDomain, redirectUri } = stateData

    const mappedIntegrationType = Object.keys(_INTEGRATION_TYPE_MAP).find((e) => _INTEGRATION_TYPE_MAP[e] === integrationType) ?? integrationType
    const payloadRedirectUri = window.location.origin + ROUTER_BASEPATH + `/integrations/callback/${mappedIntegrationType as IntegrationServiceId}`

    if (code) {
      if (step === 'dynamics_discovery_step' && integrationType === IntegrationServiceId.DYNAMICS) {
        const state = createState(redirectUri, 'dynamics_oauth_step', customDomain)
        window.location.href = `${import.meta.env.REACT_APP_IAM_API_BASE_URL}/accounts/${accountId}/user-integrations/${integrationType}/enable?redirect_uri=${payloadRedirectUri}&code=${code}&state=${state}`
        return
      }

      if (step === 'engage_discovery_step' && integrationType === IntegrationServiceId.ENGAGE) {
        const state = createState(redirectUri, 'engage_oauth_step', customDomain)
        window.location.href = `${import.meta.env.REACT_APP_IAM_API_BASE_URL}/accounts/${accountId}/user-integrations/${integrationType}/enable?redirect_uri=${payloadRedirectUri}&code=${code}&state=${state}`
        return
      }

      const payload = {
        redirectUri: payloadRedirectUri,
        code,
        ...rest,
        ...(customDomain && { customDomain }),
        ...((integrationType === IntegrationServiceId.DYNAMICS || integrationType === IntegrationServiceId.ENGAGE) && { connectType: 'oauth_custom_domain' }),
      }

      createNewUserIntegration(accountId, integrationType as IntegrationServiceId, payload)
    } else {
      navigate({
        pathname: '/account/profile-settings/integrations',
        search: createSearchParams({
          error: 'missing_code',
        }).toString(),
      })
    }
  }, [navigate, searchParams, redirecting, accountId, integrationType, createNewUserIntegration, createPayloadFromSearchParams, handleError])

  return <RedirectProgress />
}

export default IntegrationCallbackView
